import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  GitMerge,
  Loader2,
  Search,
  AlertTriangle,
  ArrowRight,
  MessageSquare,
  Paperclip,
  Users,
  Eye,
  BookOpen,
  UserPlus,
} from "lucide-react";
import { useAllOrganizationTickets, useMergePreview, useMergeTicket } from "../../hooks/useTickets";
import { useDebounce } from "../../hooks/useDebounce";
import { showToast } from "../../lib/toast";
import { getErrorMessage } from "../../lib/apiErrors";
import { TicketStatusLabels, TicketStatusColors } from "../../types/tickets";
import type { TicketResponse } from "../../types/tickets";

interface TicketMergeDialogProps {
  /** The ticket being merged away. It becomes a closed stub. */
  sourceTicketId: string;
  sourceTicketNumber: number;
  organizationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful merge, with the surviving ticket's id. */
  onMerged?: (targetTicketId: string) => void;
}

export function TicketMergeDialog({
  sourceTicketId,
  sourceTicketNumber,
  organizationId,
  open,
  onOpenChange,
  onMerged,
}: TicketMergeDialogProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTicket, setSelectedTicket] = useState<TicketResponse | null>(null);
  // A ticket's participants can open it in the customer portal, so copying this ticket's customers onto the
  // target is the agent's call (the Zendesk/Freshdesk "requester and CCs can see this" choice)
  const [copyCustomers, setCopyCustomers] = useState(true);
  const debouncedSearch = useDebounce(searchQuery, 300);

  const mergeTicketMutation = useMergeTicket();

  // Only search once the dialog is open and no target has been picked yet.
  // Uses the multi-org endpoint (GET /tickets/all) - there is no single-org
  // GET /tickets route on the backend; the organizationIds filter scopes it.
  const { data: searchResults, isLoading: isSearching } = useAllOrganizationTickets(
    {
      searchQuery: debouncedSearch || undefined,
      organizationIds: [organizationId],
      pageSize: 10,
    },
    open && !selectedTicket && debouncedSearch.trim().length > 0
  );

  const {
    data: preview,
    isLoading: isLoadingPreview,
    isError: isPreviewError,
    error: previewError,
  } = useMergePreview(
    sourceTicketId,
    selectedTicket?.id,
    organizationId,
    open && !!selectedTicket
  );

  // Reset everything when the dialog closes so it reopens clean
  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setSelectedTicket(null);
      setCopyCustomers(true);
    }
  }, [open]);

  // The current ticket can't be its own target, and an already-merged ticket
  // can't receive a merge - filter both out rather than letting the user pick
  // something the server will reject.
  const candidates = (searchResults?.tickets ?? []).filter(
    (t) => t.id !== sourceTicketId && !t.mergedIntoTicketId
  );

  const handleMerge = async () => {
    if (!selectedTicket) return;

    try {
      const result = await mergeTicketMutation.mutateAsync({
        sourceTicketId,
        targetTicketId: selectedTicket.id,
        organizationId,
        copyCustomersToTarget: copyCustomers,
      });

      showToast.success(
        `Merged into #${result.targetTicketNumber}`,
        `${result.commentsMoved} comment${result.commentsMoved === 1 ? "" : "s"} and ${result.attachmentsMoved} attachment${result.attachmentsMoved === 1 ? "" : "s"} moved.`
      );

      onOpenChange(false);
      onMerged?.(result.targetTicketId);
    } catch (error) {
      showToast.error("Merge failed", getErrorMessage(error, "Could not merge these tickets."));
    }
  };

  const isBlocked = !!preview?.blockedReason;
  const hasCustomersToCopy = !!preview && (preview.participantsToMove > 0 || preview.addsSourceCustomerAsParticipant);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85dvh] max-w-[min(32rem,calc(100vw-2rem))] flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="h-4 w-4" />
            Merge ticket #{sourceTicketNumber}
          </DialogTitle>
          <DialogDescription>
            {selectedTicket
              ? "Review what will move, then confirm."
              : "Search for the ticket this one should be merged into."}
          </DialogDescription>
        </DialogHeader>

        {!selectedTicket ? (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 pt-2">
            <div className="relative shrink-0 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by ticket number or subject..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                maxLength={500}
                autoFocus
              />
            </div>

            <div className="min-h-0 flex-1 space-y-1 overflow-y-auto overflow-x-hidden">
              {debouncedSearch.trim().length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  Start typing to find a ticket.
                </p>
              ) : isSearching ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : candidates.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  No matching tickets found.
                </p>
              ) : (
                candidates.map((ticket) => (
                  <button
                    key={ticket.id}
                    type="button"
                    onClick={() => setSelectedTicket(ticket)}
                    className="flex w-full min-w-0 items-start gap-3 rounded-lg p-2.5 text-left transition-colors hover:bg-accent"
                  >
                    <span className="text-xs font-mono text-muted-foreground pt-0.5 shrink-0">
                      #{ticket.ticketNumber}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium truncate">
                        {ticket.subject}
                      </span>
                      <span className="block text-xs text-muted-foreground truncate">
                        {ticket.customer.fullName}
                      </span>
                    </span>
                    <Badge variant="outline" className={`shrink-0 ${TicketStatusColors[ticket.status]}`}>
                      {TicketStatusLabels[ticket.status]}
                    </Badge>
                  </button>
                ))
              )}
            </div>
          </div>
        ) : (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-y-auto pt-2">
            <div className="flex min-w-0 items-center gap-2 text-sm">
              <span className="shrink-0 font-mono text-muted-foreground">#{sourceTicketNumber}</span>
              <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="shrink-0 font-mono font-medium">#{selectedTicket.ticketNumber}</span>
              <span className="truncate text-muted-foreground">{selectedTicket.subject}</span>
            </div>

            {isLoadingPreview ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : isPreviewError ? (
              <div className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3">
                <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                <p className="text-sm text-destructive">
                  {getErrorMessage(previewError, "Couldn't load the merge preview.")}
                </p>
              </div>
            ) : isBlocked ? (
              <div className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3">
                <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                <p className="text-sm text-destructive">{preview?.blockedReason}</p>
              </div>
            ) : preview ? (
              <div className="rounded-lg border p-3 space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  What will move
                </p>
                <ul className="space-y-1.5 text-sm">
                  <MoveRow icon={MessageSquare} count={preview.commentsToMove} noun="comment" />
                  <MoveRow icon={Paperclip} count={preview.attachmentsToMove} noun="attachment" />
                  {copyCustomers && (
                    <MoveRow icon={Users} count={preview.participantsToMove} noun="participant" />
                  )}
                  <MoveRow icon={Eye} count={preview.followersToMove} noun="follower" />
                  <MoveRow icon={BookOpen} count={preview.articleLinksToMove} noun="article link" />
                  {copyCustomers && preview.addsSourceCustomerAsParticipant && (
                    <li className="flex items-center gap-2 text-muted-foreground">
                      <UserPlus className="h-3.5 w-3.5 shrink-0" />
                      This ticket's customer will be added as a participant
                    </li>
                  )}
                </ul>
                {hasCustomersToCopy && (
                  <div className="flex items-start gap-2 border-t pt-2">
                    <Checkbox
                      id="merge-copy-customers"
                      checked={copyCustomers}
                      onCheckedChange={(checked) => setCopyCustomers(checked === true)}
                      className="mt-0.5"
                    />
                    <label htmlFor="merge-copy-customers" className="text-sm leading-snug cursor-pointer">
                      <span className="font-medium">Copy customers to #{selectedTicket.ticketNumber}</span>
                      <span className="block text-xs text-muted-foreground">
                        This ticket's customer and participants keep getting updates and can see #{selectedTicket.ticketNumber}{" "}
                        in the customer portal. Turn off if it belongs to an unrelated customer.
                      </span>
                    </label>
                  </div>
                )}
                <p className="text-xs text-muted-foreground pt-1 border-t">
                  Ticket #{sourceTicketNumber} will be closed and will point at #{selectedTicket.ticketNumber}.
                  This cannot be undone.
                </p>
              </div>
            ) : null}
          </div>
        )}

        <DialogFooter className="shrink-0 flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-2">
          {selectedTicket && (
            <Button
              variant="outline"
              onClick={() => setSelectedTicket(null)}
              disabled={mergeTicketMutation.isPending}
            >
              Back
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mergeTicketMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleMerge}
            disabled={
              !selectedTicket ||
              !preview ||
              isBlocked ||
              isPreviewError ||
              isLoadingPreview ||
              mergeTicketMutation.isPending
            }
          >
            {mergeTicketMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Merging...
              </>
            ) : (
              <>
                <GitMerge className="h-4 w-4 mr-2" />
                Merge
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MoveRow({
  icon: Icon,
  count,
  noun,
}: {
  icon: typeof MessageSquare;
  count: number;
  noun: string;
}) {
  return (
    <li className={`flex items-center gap-2 ${count === 0 ? "text-muted-foreground/60" : ""}`}>
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span>
        <span className="font-medium">{count}</span> {noun}
        {count === 1 ? "" : "s"}
      </span>
    </li>
  );
}
